import React, { createContext, useEffect, useReducer, useRef } from "react";
import { doc, getDoc, setDoc, updateDoc } from "firebase/firestore";

// Project import
import {
  APP_SETTINGS_RESET_STATE,
  SET_USER_LANGUAGE,
  SET_MAP_BACKGROUND_LAYER,
  SET_MAP_OVERLAY_LAYERS,
  SET_MAP_CUSTOM_LAYERS,
  SET_MAP_CATEGORIES,
  SET_MAP_CATEGORY_FILTER_STATE,
  SET_MAP_DISPLAY_TOTAL_VALUES,
  SET_LABELS_ACTIVE,
  SET_LABEL_BEHAVIOR,
  SET_TOOLTIPS_ACTIVE,
  SET_INIT_STATE,
  SET_TRIAL_SUBSCRIPTION,
  SET_MAP_DISPLAY_MAP_PERIMETER_FEATURES,
  SET_MAP_DISPLAY_ACTIVITY_NOTIFICATION,
  SET_AUTO_ACTIVATE_ACTIVITY_THEMES,
  SET_SHARE_WITH_ALL_ADMIN_ORGANIZATION,
  SET_PROJECT_SHARE_WITH_ALL_ADMIN_ORGANIZATION,
  SET_CONTACT_COMMUNICATION_SETTINGS,
  SET_USE_SYSTEM_INVENTORY_DISPLAY_REFERENCE,
  SET_APP_SUB_CONFIG_INITIAL_USER_SETTINGS_STATE,
  SET_FULL_STATE,
  SET_FEATURE_CREATION_PREFERENCE,
} from "store/reducers/actions";
import appSettingsReducer from "store/reducers/appSettings";
import { firestore as db } from "lib/firebase/firebase";
import {
  AppSettingsContextType,
  AppSettingsProps,
  FeatureCreationContext,
  FeatureCreationPreference,
} from "types/appSettings";
import { I18n } from "config/config-general";
import useConfig from "hooks/useConfig";
import useAppFunctionalityConfig from "hooks/useAppFunctionalityConfig";
import { SubscriptionTypes } from "types/auth";

const initialState: AppSettingsProps = {
  trialSubscription: "unknown",
  initializing: true,
  userLanguage: null,
  appSubConfigInitialUserSettingsState: "not-initialized",
  map: {
    backgroundLayer: "googleTerrain",
    overlayLayers: null,
    customLayers: null,
    categories: null,
    categoryFilterState: null,
    pointLabelsActive: true,
    lineLabelsActive: false,
    polygonLabelsActive: true,
    pointTooltipsActive: true,
    lineTooltipsActive: true,
    labelBackgroundActive: false,
    labelZoomDependenceActive: true,
    polygonTooltipsActive: true,
    displayTotalValues: false,
    displayMapPerimeterFeatures: false,
    displayActivityNotifications: false,
    autoActivateActivityThemes: null,
  },
  sharedata: {
    mapShareWithAllAdminOrganization: false,
    projectShareWithAllAdminOrganization: false,
  },
  contactCommunication: {
    sharing: true,
    subscriptionChanges: true,
    buyerSellerApprovalFlow: true,
    activityResponsible: true,
    noteMentions: true,
  },
  inventory: {
    useSystemDisplayReference: true,
  },
  featureCreation: {
    version: 1,
    preferences: {},
  },
};

const AppSettingsContext = createContext<AppSettingsContextType | null>(null);

export function AppSettingsProvider({ children }: { children: React.ReactNode }) {
  // --- Hooks --- //
  // const { isLoggedIn, user, userOrg, orgPrivateData } = useAuth();
  const { onChangeLocalization } = useConfig();
  const { config: appConfig, isDomainReadOnly } = useAppFunctionalityConfig();
  const [state, dispatch] = useReducer(appSettingsReducer, initialState);

  // --- State and reference --- //
  const userIdRef = useRef<string | null | undefined>(undefined);
  const orgIdRef = useRef<string | null | undefined>(undefined);
  const userAppSettingsFetchedRef = useRef<boolean>(false);
  const orgAppSettingsFetchedRef = useRef<boolean>(false);
  const orgHasInventoryDisplayReferenceSettingRef = useRef<boolean>(false);
  const configuredUseSystemInventoryDisplayReference =
    appConfig.accountPage.useSystemInventoryDisplayReference;
  // Existing browser sessions can retain a state from before this optional
  // preference was introduced. Keep the public context and Firestore payload valid.
  const featureCreation = state.featureCreation ?? initialState.featureCreation;

  // --- Effects --- //
  useEffect(() => {
    return () => {
      resetState();
    };
  }, []);

  useEffect(() => {
    // Set initializing flag to false when both user and org app settings have been fetched
    if (userIdRef.current !== undefined && orgIdRef.current !== undefined) {
      dispatch({ type: SET_INIT_STATE, payload: false });
    } else {
      // If either userId or orgId is still undefined, set a timeout to update the state
      setTimeout(() => {
        dispatch({ type: SET_INIT_STATE, payload: false });
      }, 2000);
    }
  }, [userIdRef.current, orgIdRef.current]);

  useEffect(() => {
    if (orgIdRef.current && !orgHasInventoryDisplayReferenceSettingRef.current) {
      dispatch({
        type: SET_USE_SYSTEM_INVENTORY_DISPLAY_REFERENCE,
        payload: configuredUseSystemInventoryDisplayReference,
      });
    }
  }, [configuredUseSystemInventoryDisplayReference]);

  // Set user and org app settings on firestore
  useEffect(() => {
    if (userIdRef.current) {
      // derive user app settings from state and remove itmes not needed
      const { ...restMap } = state.map;
      // Settings object to be updated
      let settingsObj: any = {
        map: restMap,
        featureCreation,
      };
      if (state.userLanguage) {
        settingsObj.userLanguage = state.userLanguage;
      }
      if (state.trialSubscription) {
        settingsObj.trialSubscription = state.trialSubscription;
      }
      if (state.appSubConfigInitialUserSettingsState) {
        settingsObj.appConfigSubInitializedState = state.appSubConfigInitialUserSettingsState;
      }
      updateUserAppSettings(userIdRef.current, settingsObj);
      userAppSettingsFetchedRef.current = true;
    }
    if (orgIdRef.current) {
      // derive org app settings from state and remove itmes not needed
      const { ...restSharedata } = state.sharedata;
      const { ...restInventory } = state.inventory;
      // Settings object to be updated
      let settingsObj: any = {
        sharedata: restSharedata,
        inventory: restInventory,
      };
      updateOrgAppSettings(orgIdRef.current, settingsObj);
      orgAppSettingsFetchedRef.current = true;
    }
  }, [state]);

  // --- Actions --- //
  const resetState = () => {
    // Internal state
    userAppSettingsFetchedRef.current = false;
    orgHasInventoryDisplayReferenceSettingRef.current = false;
    // Reducer state
    dispatch({
      type: APP_SETTINGS_RESET_STATE,
      payload: null,
    });
  };

  const setTrialSubscription = (subscription: SubscriptionTypes) => {
    dispatch({
      type: SET_TRIAL_SUBSCRIPTION,
      payload: subscription,
    });
  };

  const setUserLanguage = (lang: I18n | null) => {
    dispatch({
      type: SET_USER_LANGUAGE,
      payload: lang,
    });
  };

  const setAppSubConfigInitialUserSettingsState = (state: AppSettingsProps["appSubConfigInitialUserSettingsState"]) => {
    dispatch({
      type: SET_APP_SUB_CONFIG_INITIAL_USER_SETTINGS_STATE,
      payload: state,
    });
  }

  const setMapBackgroundLayer = (layer: string | null) => {
    dispatch({
      type: SET_MAP_BACKGROUND_LAYER,
      payload: layer,
    });
  };

  const setMapOverlayLayers = (layers: string[] | null) => {
    dispatch({
      type: SET_MAP_OVERLAY_LAYERS,
      payload: layers,
    });
  };

  const setMapCustomLayers = (layers: string[] | null) => {
    dispatch({
      type: SET_MAP_CUSTOM_LAYERS,
      payload: layers,
    });
  };

  const setMapCategories = (categories: string[] | null) => {
    dispatch({
      type: SET_MAP_CATEGORIES,
      payload: categories,
    });
  };

  const setMapCategoriesFilterState = (categoryFilterState: any) => {
    dispatch({
      type: SET_MAP_CATEGORY_FILTER_STATE,
      payload: categoryFilterState,
    });
  };

  const setMapDisplayTotalValues = (displayTotalValues: boolean) => {
    dispatch({
      type: SET_MAP_DISPLAY_TOTAL_VALUES,
      payload: displayTotalValues,
    });
  };

  const setLabelsActive = (labels: {
    pointLabelsActive: boolean;
    lineLabelsActive: boolean;
    polygonLabelsActive: boolean;
  }) => {
    dispatch({
      type: SET_LABELS_ACTIVE,
      payload: labels,
    });
  };

  const setLabelBeavior = (labelBehavior: {
    labelBackgroundActive: boolean;
    labelZoomDependenceActive: boolean;
  }) => {
    dispatch({
      type: SET_LABEL_BEHAVIOR,
      payload: labelBehavior,
    });
  };

  const setTooltipsActive = (tooltips: {
    pointTooltipsActive: boolean;
    lineTooltipsActive: boolean;
    polygonTooltipsActive: boolean;
  }) => {
    dispatch({
      type: SET_TOOLTIPS_ACTIVE,
      payload: tooltips,
    });
  };

  const setMapDisplayMapPerimeterFeatures = (displayMapPerimeterFeatures: boolean) => {
    dispatch({
      type: SET_MAP_DISPLAY_MAP_PERIMETER_FEATURES,
      payload: displayMapPerimeterFeatures,
    });
  };

  const setAutoActivateActivityThemes = (enabled: boolean) => {
    dispatch({ type: SET_AUTO_ACTIVATE_ACTIVITY_THEMES, payload: enabled });
  };

  const setMapDisplayActivityNotification = (displayActivityNotifications: boolean) => {
    dispatch({
      type: SET_MAP_DISPLAY_ACTIVITY_NOTIFICATION,
      payload: displayActivityNotifications,
    });
  };

  const setMapShareWithAllAdminOrganization = (mapShareWithAllAdminOrganization: boolean) => {
    dispatch({
      type: SET_SHARE_WITH_ALL_ADMIN_ORGANIZATION,
      payload: mapShareWithAllAdminOrganization,
    });
  };

  const setProjectShareWithAllAdminOrganization = (
    projectShareWithAllAdminOrganization: boolean,
  ) => {
    dispatch({
      type: SET_PROJECT_SHARE_WITH_ALL_ADMIN_ORGANIZATION,
      payload: projectShareWithAllAdminOrganization,
    });
  };

  const setContactCommunicationSettings = async (
    settings: Partial<AppSettingsProps["contactCommunication"]>,
  ) => {
    const orgId = orgIdRef.current;
    if (!orgId || isDomainReadOnly()) return;
    try {
      // Only explicit edits persist these preferences. Merging individual fields
      // prevents a stale session from restoring another user's disabled toggle.
      await setDoc(doc(db, `orgs/${orgId}/private_data/appSettings`), {
        contactCommunication: settings,
      }, { merge: true });
      if (orgIdRef.current === orgId) {
        dispatch({ type: SET_CONTACT_COMMUNICATION_SETTINGS, payload: settings });
      }
    } catch (error) {
      console.error("Error updating contact communication settings: ", error);
    }
  };

  const setUseSystemInventoryDisplayReference = (
    enabled: boolean,
  ) => {
    dispatch({ type: SET_USE_SYSTEM_INVENTORY_DISPLAY_REFERENCE, payload: enabled });
  };

  const setFeatureCreationPreference = (
    context: FeatureCreationContext,
    preference: Partial<FeatureCreationPreference>,
  ) => {
    dispatch({ type: SET_FEATURE_CREATION_PREFERENCE, payload: { context, preference } });
  };

  const setFullState = (fullState: Partial<AppSettingsProps>) => {
    dispatch({
      type: SET_FULL_STATE,
      payload: fullState,
    });
  }

  // --- Sync context with firestore --- //
  const fetchUserAppSettings = async (userId: string) => {
    try {
      const docRef = doc(db, `users/${userId}/private_data/appSettings`);
      const docSnap = await getDoc(docRef);
      if (!docSnap.exists()) {
        dispatch({ type: SET_AUTO_ACTIVATE_ACTIVITY_THEMES, payload: null });
      }
      if (docSnap.exists()) {
        const data = docSnap.data();
        if (data.featureCreation?.version === 1) {
          dispatch({
            type: SET_FULL_STATE,
            payload: { featureCreation: data.featureCreation },
          });
        }
        // Set trial subscription
        const trialSubscription = data.trialSubscription ? data.trialSubscription : "unknown";
        const appConfigSubInitializedState = data.appConfigSubInitializedState ? data.appConfigSubInitializedState : "not-initialized";
        dispatch({
          type: SET_TRIAL_SUBSCRIPTION,
          payload: trialSubscription,
        });
        // Set app sub config initial user settings state to initialized to indicate that the user settings have been fetched and applied to the context state
        dispatch({
          type: SET_APP_SUB_CONFIG_INITIAL_USER_SETTINGS_STATE,
          payload: appConfigSubInitializedState,
        });
        // Set localization
        if (data.userLanguage) {
          dispatch({
            type: SET_USER_LANGUAGE,
            payload: data.userLanguage,
          });
          onChangeLocalization(data.userLanguage);
        }
        // Set background layer
        if (data.map.backgroundLayer) {
          dispatch({
            type: SET_MAP_BACKGROUND_LAYER,
            payload: data.map.backgroundLayer,
          });
        }
        // Set overalys
        if (data.map.overlayLayers) {
          dispatch({
            type: SET_MAP_OVERLAY_LAYERS,
            payload: data.map.overlayLayers,
          });
        }
        // Set custom layers
        if (data.map.customLayers) {
          dispatch({
            type: SET_MAP_CUSTOM_LAYERS,
            payload: data.map.customLayers,
          });
        }
        // Categories
        if (data.map.categories) {
          dispatch({
            type: SET_MAP_CATEGORIES,
            payload: data.map.categories,
          });
        }
        // Categories filter state
        if (data.map.categoryFilterState) {
          dispatch({
            type: SET_MAP_CATEGORY_FILTER_STATE,
            payload: data.map.categoryFilterState,
          });
        }
        // Set data display
        dispatch({
          type: SET_MAP_DISPLAY_TOTAL_VALUES,
          payload:
            data.map.displayTotalValues !== undefined
              ? data.map.displayTotalValues
              : initialState.map.displayTotalValues,
        });
        // Set labels
        dispatch({
          type: SET_LABELS_ACTIVE,
          payload: {
            pointLabelsActive:
              data.map.pointLabelsActive !== undefined
                ? data.map.pointLabelsActive
                : initialState.map.pointLabelsActive,
            lineLabelsActive:
              data.map.lineLabelsActive !== undefined
                ? data.map.lineLabelsActive
                : initialState.map.lineLabelsActive,
            polygonLabelsActive:
              data.map.polygonLabelsActive !== undefined
                ? data.map.polygonLabelsActive
                : initialState.map.polygonLabelsActive,
          },
        });
        // Set label behavior
        dispatch({
          type: SET_LABEL_BEHAVIOR,
          payload: {
            labelBackgroundActive:
              data.map.labelBackgroundActive !== undefined
                ? data.map.labelBackgroundActive
                : initialState.map.labelBackgroundActive,
            labelZoomDependenceActive:
              data.map.labelZoomDependenceActive !== undefined
                ? data.map.labelZoomDependenceActive
                : initialState.map.labelZoomDependenceActive,
          },
        });
        // Set tooltips
        dispatch({
          type: SET_TOOLTIPS_ACTIVE,
          payload: {
            pointTooltipsActive:
              data.map.pointTooltipsActive !== undefined
                ? data.map.pointTooltipsActive
                : initialState.map.pointTooltipsActive,
            lineTooltipsActive:
              data.map.lineTooltipsActive !== undefined
                ? data.map.lineTooltipsActive
                : initialState.map.lineTooltipsActive,
            polygonTooltipsActive:
              data.map.polygonTooltipsActive !== undefined
                ? data.map.polygonTooltipsActive
                : initialState.map.polygonTooltipsActive,
          },
        });
        // Set display map perimeter features
        dispatch({
          type: SET_MAP_DISPLAY_MAP_PERIMETER_FEATURES,
          payload:
            data.map.displayMapPerimeterFeatures !== undefined
              ? data.map.displayMapPerimeterFeatures
              : initialState.map.displayMapPerimeterFeatures,
        });
        // Keep an absent preference distinct from an explicit false value.
        dispatch({
          type: SET_AUTO_ACTIVATE_ACTIVITY_THEMES,
          payload: typeof data.map?.autoActivateActivityThemes === "boolean"
            ? data.map.autoActivateActivityThemes
            : null,
        });
        // Set display activity notification
        dispatch({
          type: SET_MAP_DISPLAY_ACTIVITY_NOTIFICATION,
          payload:
            data.map.displayActivityNotifications !== undefined
              ? data.map.displayActivityNotifications
              : initialState.map.displayActivityNotifications,
        });
      }
    } catch (error) {
      console.error("Error fetching user settings: ", error);
    }
    userIdRef.current = userId ? userId : null;
  };

  const fetchOrgAppSettings = async (orgId: string) => {
    try {
      const docRef = doc(db, `orgs/${orgId}/private_data/appSettings`);
      const docSnap = await getDoc(docRef);
      let useSystemDisplayReference = configuredUseSystemInventoryDisplayReference;
      if (docSnap.exists()) {
        const data = docSnap.data();
        dispatch({
          type: SET_SHARE_WITH_ALL_ADMIN_ORGANIZATION,
          payload:
            data.sharedata && data.sharedata.mapShareWithAllAdminOrganization !== undefined
              ? data.sharedata.mapShareWithAllAdminOrganization
              : initialState.sharedata.mapShareWithAllAdminOrganization,
        });
        dispatch({
          type: SET_PROJECT_SHARE_WITH_ALL_ADMIN_ORGANIZATION,
          payload:
            data.sharedata && data.sharedata.projectShareWithAllAdminOrganization !== undefined
              ? data.sharedata.projectShareWithAllAdminOrganization
              : initialState.sharedata.projectShareWithAllAdminOrganization,
        });
        dispatch({
          type: SET_CONTACT_COMMUNICATION_SETTINGS,
          payload: {
            ...initialState.contactCommunication,
            ...data.contactCommunication,
          },
        });
        if (typeof data.inventory?.useSystemDisplayReference === "boolean") {
          orgHasInventoryDisplayReferenceSettingRef.current = true;
          useSystemDisplayReference = data.inventory.useSystemDisplayReference;
        } else if (data.inventory?.displayReferenceMode === "manual") {
          orgHasInventoryDisplayReferenceSettingRef.current = true;
          useSystemDisplayReference = false;
        }
      }
      dispatch({
        type: SET_USE_SYSTEM_INVENTORY_DISPLAY_REFERENCE,
        payload: useSystemDisplayReference,
      });
    } catch (error) {
      console.error("Error fetching organization settings: ", error);
    }

    orgIdRef.current = orgId ? orgId : null;
  };

  const updateUserAppSettings = async (userId: string, data: any) => {
    if (isDomainReadOnly()) return;
    try {
      const docRef = doc(db, `users/${userId}/private_data/appSettings`);
      const docSnap = await getDoc(docRef);
      if (isDomainReadOnly()) return;
      if (!docSnap.exists()) {
        await setDoc(docRef, {
          ...data,
        });
      } else {
        await updateDoc(docRef, {
          ...data,
        });
      }
    } catch (error) {
      console.error("Error updating user settings: ", error);
    }
  };

  const updateOrgAppSettings = async (orgId: string, data: any) => {
    if (isDomainReadOnly()) return;
    try {
      const docRef = doc(db, `orgs/${orgId}/private_data/appSettings`);
      // Merge even on creation: another client may create preferences concurrently.
      await setDoc(docRef, data, { merge: true });
    } catch (error) {
      console.error("Error updating organization settings: ", error);
    }
  };

  return (
    <AppSettingsContext.Provider
      value={{
        ...state,
        featureCreation,
        setTrialSubscription,
        setUserLanguage,
        setAppSubConfigInitialUserSettingsState,
        setFullState,
        setMapBackgroundLayer,
        setMapOverlayLayers,
        setMapCustomLayers,
        setMapCategories,
        setMapCategoriesFilterState,
        setMapDisplayTotalValues,
        setLabelsActive,
        setLabelBeavior,
        setTooltipsActive,
        setMapDisplayMapPerimeterFeatures,
        setMapDisplayActivityNotification,
        setAutoActivateActivityThemes,
        setMapShareWithAllAdminOrganization,
        setProjectShareWithAllAdminOrganization,
        setContactCommunicationSettings,
        setUseSystemInventoryDisplayReference,
        setFeatureCreationPreference,
        updateUserAppSettings,
        updateOrgAppSettings,
        fetchUserAppSettings,
        fetchOrgAppSettings,
      }}
    >
      {children}
    </AppSettingsContext.Provider>
  );
}

export default AppSettingsContext;
